/**
 * Signaler un problème (conception/signaler.md, décision 146) : toute
 * personne connectée dit « il y a un problème » depuis n'importe quel écran ;
 * le Super administrateur trie et traite. Ce n'est pas l'écart d'offre
 * (`signalements-offre.ts`), qui parle d'une personne : ici, de la plateforme.
 */
import { z } from 'zod';
import { schemaPage, schemaPagination } from './administration.js';
/** R5 — cinq catégories, dans les mots de qui signale. */
export const CATEGORIES_SIGNALEMENT = [
    'NE_MARCHE_PAS',
    'AFFICHAGE',
    'LENTEUR',
    'MANQUE',
    'AUTRE',
];
export const LIBELLE_CATEGORIE_SIGNALEMENT = {
    NE_MARCHE_PAS: 'Ça ne marche pas',
    AFFICHAGE: 'Ça s’affiche mal',
    LENTEUR: 'Ça va trop lentement',
    MANQUE: 'Il manque quelque chose',
    AUTRE: 'Autre',
};
/** R10 — le cycle de traitement, tenu par le Super administrateur. */
export const STATUTS_SIGNALEMENT = ['NOUVEAU', 'EN_COURS', 'RESOLU', 'CLASSE', 'REOUVERT'];
/** R10 — les trois mots du tri de 17 h de la recette. Fixée au tri, jamais par l'auteur. */
export const GRAVITES_SIGNALEMENT = ['BLOQUANT', 'GENANT', 'APRES'];
export const TEXTE_SIGNALEMENT_MIN = 10;
export const TEXTE_SIGNALEMENT_MAX = 2000;
/**
 * R5, R8 — ce que le navigateur envoie. L'identité, le rôle, le site, l'heure
 * et la version, le serveur les recalcule : il ne croit pas le navigateur.
 * Le chemin de l'écran est regabarité côté serveur (R7).
 */
export const schemaCreationSignalement = z
    .object({
    categorie: z.enum(CATEGORIES_SIGNALEMENT),
    texte: z.string().trim().min(TEXTE_SIGNALEMENT_MIN).max(TEXTE_SIGNALEMENT_MAX),
    ecran: z.string().max(500),
    /** Navigateur et taille d'écran, en une ligne. */
    navigateur: z.string().trim().max(300),
    /** La dernière erreur du navigateur, si elle date de moins de 5 minutes. */
    derniereErreur: z
        .object({
        evenementId: z.string().trim().max(64).nullable(),
        message: z.string().max(1000),
    })
        .strict()
        .optional(),
    /** Le `requestId` de la dernière requête en échec : il retrouve la ligne des journaux. */
    requeteIdEchec: z.string().trim().max(100).optional(),
})
    .strict();
/** La réponse à l'auteur : son numéro, rien d'autre. */
export const schemaSignalementCree = z.object({
    id: z.uuid(),
    numero: z.string(),
});
/** La liste du Super administrateur : les plus récents d'abord, filtrable. */
export const schemaFiltresSignalements = schemaPagination.extend({
    statut: z.enum(STATUTS_SIGNALEMENT).optional(),
    gravite: z.enum(GRAVITES_SIGNALEMENT).optional(),
    categorie: z.enum(CATEGORIES_SIGNALEMENT).optional(),
});
/** Un signalement tel que le Super administrateur le lit et le traite. */
export const schemaSignalement = z.object({
    id: z.uuid(),
    numero: z.string(),
    categorie: z.enum(CATEGORIES_SIGNALEMENT),
    texte: z.string(),
    ecran: z.string(),
    versionApp: z.string(),
    navigateur: z.string(),
    derniereErreurEvenement: z.string().nullable(),
    derniereErreurMessage: z.string().nullable(),
    requeteIdEchec: z.string().nullable(),
    /** Le nom se lit par jointure : il n'est pas copié dans le signalement. */
    auteur: z
        .object({ nom: z.string(), prenom: z.string().nullable(), actif: z.boolean() })
        .nullable(),
    roleCode: z.string(),
    site: z.object({ id: z.uuid(), nom: z.string() }).nullable(),
    statut: z.enum(STATUTS_SIGNALEMENT),
    gravite: z.enum(GRAVITES_SIGNALEMENT).nullable(),
    noteInterne: z.string().nullable(),
    reponse: z.string().nullable(),
    doublonDe: z.string().nullable(),
    version: z.number().int(),
    creeLe: z.string(),
    traiteLe: z.string().nullable(),
});
export const schemaPageSignalements = schemaPage(schemaSignalement);
const texteLibre = z.string().trim().max(2000);
/**
 * R10 — le traitement. `version` est celle lue : un autre Super admin passé
 * entre-temps fait refuser l'enregistrement (409). `doublonDe` : le numéro
 * `SIG-0042` du signalement dont celui-ci est le doublon.
 */
export const schemaTraitementSignalement = z
    .object({
    version: z.number().int().min(1),
    statut: z.enum(STATUTS_SIGNALEMENT).optional(),
    gravite: z.enum(GRAVITES_SIGNALEMENT).nullable().optional(),
    noteInterne: texteLibre.nullable().optional(),
    reponse: texteLibre.nullable().optional(),
    doublonDe: z
        .string()
        .trim()
        .regex(/^SIG-\d+$/i, 'Un numéro de la forme SIG-0042.')
        .nullable()
        .optional(),
})
    .strict();
/**
 * R11, décision 177 — un signalement tel que son auteur le relit dans « Mes
 * signalements ». **Jamais la note interne**, ni le contexte technique : la
 * forme est fermée (`strict`), un champ de plus ne passe pas.
 */
export const schemaMonSignalement = z
    .object({
    id: z.uuid(),
    numero: z.string(),
    creeLe: z.string(),
    categorie: z.enum(CATEGORIES_SIGNALEMENT),
    texte: z.string(),
    statut: z.enum(STATUTS_SIGNALEMENT),
    gravite: z.enum(GRAVITES_SIGNALEMENT).nullable(),
    reponse: z.string().nullable(),
    traiteLe: z.string().nullable(),
    /** Une réponse ou une clôture que l'auteur n'a pas encore lue. */
    nonLu: z.boolean(),
})
    .strict();
export const schemaPageMesSignalements = schemaPage(schemaMonSignalement);
/** Le compteur de « Mes signalements » dans le menu. */
export const schemaMesSignalementsNonLus = z.object({ nombre: z.number().int().min(0) });
//# sourceMappingURL=signalements.js.map